import QuillResizeImage from 'quill-resize-image';
import React, { useEffect, useRef } from 'react';
import ReactQuill from 'react-quill';
import { Quill } from 'react-quill';
import styled from 'styled-components';

interface ReactQuillProps {
  value?: string;
  onChange?: (...args: any[]) => void;
  style?: React.CSSProperties;
  [key: string]: any;
}

Quill.register('modules/resize', QuillResizeImage);

const modules = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
    [
      { list: 'ordered' },
      { list: 'bullet' },
      { indent: '-1' },
      { indent: '+1' },
    ],
    ['link', 'image'],
    ['clean'],
  ],
  resize: {
    locale: {},
  },
};

const formats = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'blockquote',
  'list',
  'bullet',
  'indent',
  'link',
  'image',
];

export const ArticleEditor: React.FC<ReactQuillProps> = ({
  value,
  onChange,
  style,
  ...props
}) => {
  const quillRef = useRef<ReactQuill | null>(null);

  const handleResize = React.useCallback(() => {
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      const content = editor.root.innerHTML;
      const delta = editor.getContents(); // Get the current contents as a Delta

      // Create an UnprivilegedEditor object
      const unprivilegedEditor = {
        getHTML: () => content,
        getText: editor.getText.bind(editor),
        getLength: editor.getLength.bind(editor),
        getBounds: editor.getBounds.bind(editor),
        getSelection: editor.getSelection.bind(editor),
        getContents: editor.getContents.bind(editor),
      };

      if (onChange) {
        onChange(content, delta, 'user', unprivilegedEditor);
      }
    }
  }, [onChange]);

  useEffect(() => {
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      editor.on('text-change', handleResize);
    }
  }, [handleResize]);

  return (
    <Wrapper>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        modules={modules}
        formats={formats}
        value={value}
        style={{ height: '620px', ...style }}
        {...props}
      />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  margin-bottom: 40px;

  /* Match the font with the rest of the form - includes Chinese fonts */
  .ql-editor {
    font-family:
      'Microsoft YaHei',
      '微软雅黑',
      -apple-system,
      BlinkMacSystemFont,
      'Segoe UI',
      'PingFang SC',
      'Hiragino Sans GB',
      Helvetica,
      Arial,
      sans-serif,
      'SimHei',
      '黑体',
      'Apple Color Emoji',
      'Segoe UI Emoji',
      'Segoe UI Symbol';
    font-size: 14px;
    line-height: 1.6;
  }

  /* Style the toolbar to match */
  .ql-toolbar {
    font-family:
      'Microsoft YaHei',
      '微软雅黑',
      -apple-system,
      BlinkMacSystemFont,
      'Segoe UI',
      'PingFang SC',
      'Hiragino Sans GB',
      Helvetica,
      Arial,
      sans-serif;
    border-radius: 8px 8px 0 0;
    background-color: #f7fafc;
    border-color: #e2e8f0;
  }

  /* Style the editor container */
  .ql-container {
    border-radius: 0 0 8px 8px;
    border-color: #e2e8f0;
    font-family:
      'Microsoft YaHei',
      '微软雅黑',
      -apple-system,
      BlinkMacSystemFont,
      'Segoe UI',
      'PingFang SC',
      'Hiragino Sans GB',
      Helvetica,
      Arial,
      sans-serif;
  }

  /* Ensure consistent font in all editor states */
  .ql-editor p,
  .ql-editor h1,
  .ql-editor h2,
  .ql-editor h3,
  .ql-editor ul,
  .ql-editor ol,
  .ql-editor li {
    font-family: inherit;
  }
`;
